<script setup lang="ts">
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import {
    type SearchMode,
    type PaxCount,
    type PaxCapacityInfo,
} from '@utils/types';
import { type ReserveType } from '@utils/itinerary';
import { MrSelectInput } from '#components';
import { type PaxModel } from '@search/types';

interface IProps {
    paxes?: PaxModel[];
    suffix?: string;
    prefix?: string;
    unit?: string;
    disabled?: boolean;
    paxesMaxAlert?: string;
    paxErrorMessage?: string;
    capacityInfo?: PaxCapacityInfo;
    reserveType: ReserveType;
    searchMode: SearchMode;
}
const {
    paxes = [],
    prefix = '',
    paxesMaxAlert,
    suffix = '',
    unit = 'مسافر',
    disabled,
    searchMode,
    reserveType,
} = defineProps<IProps>();
const emit = defineEmits<{
    done: [];
    open: [];
    btnClose: [];
    'update:paxModel': [item: PaxCount, value: number, index: number];
    delete: [index: number];
    close: [];
}>();
const analyticsStore = useAnalyticsStore();
const router = useRouter();
function backButtonClicked() {
    router.back();
    analyticsStore.trackPosthog('search-form-back', {
        reserveType,
        searchMode,
        inputComponent: 'pax-picker',
    });
}
const select = ref<InstanceType<typeof MrSelectInput>>();
const isLocked = useScrollLock(
    typeof document === 'undefined' ? null : document.body
);
const { isMobile } = useSize();
const { addEventListener, emitListener } = addListener();
const title = computed(() => `${prefix}${totalCount.value} ${unit}${suffix}`);
const totalCount = computed(() =>
    paxes.reduce(
        (total, p) =>
            total +
            p.paxList.reduce((total, item) => {
                if (!item.countless) {
                    return total + item.count;
                } else {
                    return total;
                }
            }, 0),
        0
    )
);
const scrollLock = computed(() => !!select.value?.opened && isMobile.value);
const open = () => select.value?.open();
const close = () => select.value?.close();
const confirm = () => {
    close();
    onDone();
};
const closeButtonClicked = () => {
    onBtnClose();
    close();
};
const toggle = () => select.value?.toggle();
const focus = () => {
    open();
    return true;
};
const hide = () => close();
const onDone = () => {
    emit('done');
    emitListener('done');
};
const onOpen = () => {
    emit('open');
    emitListener('open');
};
const onBtnClose = () => {
    analyticsStore.trackPosthog('search-form-close', {
        reserveType,
        searchMode,
        inputComponent: 'pax-picker',
    });
    emit('btnClose');
    emitListener('btn-close');
};
watch(scrollLock, v => {
    isLocked.value = v;
});
defineExpose({
    addEventListener,
    hide,
    focus,
    open,
    close,
    toggle,
});
</script>

<template>
    <mr-select-input
        ref="select"
        :model-value="title"
        class="count-selector-container"
        hide-description
        mobile-modal
        paging-modal-animation
        placeholder="انتخاب تعداد"
        @open="onOpen"
        @close="emit('close')"
    >
        <template #header>
            <mr-modal-header
                title="انتخاب مسافر"
                @back="backButtonClicked"
                @close="closeButtonClicked"
            />
        </template>
        <div class="count-select-modal" @click.stop @touchstart.stop>
            <template v-for="(pax, i) in paxes" :key="`title-${i}`">
                <mr-divider v-if="i > 0" class="mr-divider" />
                <div class="title-container">
                    <div v-if="pax.title" class="room-title">
                        {{ pax.title }}
                    </div>
                    <mr-button
                        v-if="pax.deleteAble"
                        variant="transparent"
                        size="small"
                        :icon-button="{
                            icon: faTrashCan,
                            color: 'var(--red-400)',
                        }"
                        @click="!disabled && emit('delete', i)"
                    />
                </div>
                <template
                    v-for="item in pax.paxList"
                    :key="`counter-${item.code}-${i}`"
                >
                    <mr-counter
                        class="pax-counter"
                        :model-value="item.count"
                        :unit="item.title"
                        :label="item.subtitle"
                        :min="capacityInfo?.[item.code]?.min || 0"
                        :max="21"
                        :size="{ mobile: 'big', desktop: 'small' }"
                        desktop-row
                        @update:model-value="
                            !disabled &&
                                emit('update:paxModel', item, $event, i)
                        "
                    />
                </template>
            </template>
            <slot />
            <Transition v-if="paxesMaxAlert" name="fade">
                <mr-flash-message type="error" :message="paxesMaxAlert" />
            </Transition>
            <Transition name="fade">
                <mr-flash-message
                    v-if="paxErrorMessage"
                    type="error"
                    :message="paxErrorMessage"
                />
            </Transition>
        </div>
        <template #footer>
            <div class="confirm-wrapper">
                <mr-button class="confirm-btn" full-width @click="confirm">
                    تأیید
                </mr-button>
            </div>
        </template>
    </mr-select-input>
</template>
<style lang="scss" scoped>
.count-selector-container {
    position: relative;
}

.count-select-modal {
    display: flex;
    flex-direction: column;
    width: 350px;
    max-height: 550px;
    overflow-y: auto;
    padding: 20px;
    .mr-divider {
        margin: 0 0 12px 0;
    }
    @include mobile-only {
        max-height: 100vh;
    }

    .title-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 12px;
        .room-title {
            @include tg-l(usePalette(), 700);
        }
    }
    .pax-counter {
        margin-bottom: 12px;
    }
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 200ms ease-out;
    }
    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }
}

.confirm-wrapper {
    display: none;
}

@media (max-width: $app-mobile-max-width) {
    @include v-transition-horizontal-paging;
    .confirm-wrapper {
        @include shadow(2);
        display: block;
        width: 100%;
        height: 68px;
        padding: 8px;
        background-color: white;
    }

    .count-select-modal {
        flex: 1;
        padding: 20px;
        overflow-y: scroll;
        overflow-x: hidden;
        width: 100%;
    }
}
</style>
